"use client";
import React, { useRef, useState } from "react";
import { IFilter } from "@/api/AppApi/services/filters/interfaces/IGetFilters";
import FilterChipDropdown from "./FilterChipDropdown";

interface FilterChipProps {
  filter: IFilter;
  selectedValues: string[];
  onSelectionChange: (key: string, values: string[]) => void;
}

const FilterChip: React.FC<FilterChipProps> = ({
  filter,
  selectedValues,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);
  
  const { configurations, data } = filter;
  const { title, key } = configurations;
  
  const isActive = selectedValues && selectedValues.length > 0 && selectedValues.some(v => v.trim() !== "");
  
  const getDisplayText = (): string => {
    if (!isActive) {
      return title;
    }
    
    if (configurations.type === "range_slider" && selectedValues.length >= 2) {
      const min = parseFloat(selectedValues[0]);
      const max = parseFloat(selectedValues[1]);
      return `${title}: ${min.toFixed(0)} - ${max.toFixed(0)}`;
    }
    
    if (selectedValues.length === 1 && data && data.length > 0) {
      const selectedItem = data.find(item => item.key === selectedValues[0]);
      if (selectedItem) {
        return `${title}: ${selectedItem.value}`;
      }
    }
    
    if (selectedValues.length > 1) {
      return `${title} (${selectedValues.length})`;
    }
    
    return title;
  };
  
  const handleClick = () => {
    setIsOpen(!isOpen);
  };
  
  const handleClose = () => {
    setIsOpen(false);
  };
  
  const handleApply = (values: string[]) => {
    onSelectionChange(key, values);
  };
  
  return (
    <>
      <button
        ref={chipRef}
        id={`filter-chip-${key}`}
        className={`filter-chip ${isActive ? 'active' : ''}`}
        onClick={handleClick}
        aria-label={`Filter by ${title}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span 
          className="filter-chip-text"
          title={getDisplayText()}
        >
          {getDisplayText()}
        </span>
        
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      
      <FilterChipDropdown
        filter={filter}
        selectedValues={selectedValues}
        onApply={handleApply}
        onClose={handleClose}
        isOpen={isOpen}
        anchorElement={chipRef.current}
      />
    </>
  );
};

export default FilterChip;
